import React from "react";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "@components/Common/ErrorToast";

import { ScanToContext } from "..";

type UseMultiCustomIconsOptions = {
    // The saved slice of redux state for this tab — looked up for
    // `customIconFiles`, then `customIconUrls`, then `customIconUrl`
    // (legacy single-value shape) to seed the initial array on mount.
    saved: any;
    // Section icon key the consumer wants to fall back to when the user
    // removes their last custom icon (e.g. "tag" for Offres / Actions /
    // Publicites, "cart" for MaCommande).
    fallbackIconKey: string;
    // The consumer-owned setter for `selectedIcon`. Called by
    // `removeCustomIcon` when the active slot is removed and no other
    // custom icon remains, to switch the visible icon back to a default.
    setSelectedIcon: (key: string) => void;
};

type UseMultiCustomIconsResult = {
    customIconUrls: string[];
    customIconFilenames: string[];
    selectedCustomIdx: number;
    setSelectedCustomIdx: React.Dispatch<React.SetStateAction<number>>;
    // Active URL — derived from `customIconUrls[selectedCustomIdx]`.
    customIconUrl: string;
    iconFileInputRef: React.RefObject<HTMLInputElement>;
    handleIconUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    removeCustomIcon: (idx?: number) => void;
    // Revoke every custom blob URL and reset the array / filenames /
    // selected idx — used by per-tab `resetAll` handlers.
    resetCustomIcons: () => void;
};

const VALID_ICON_MIME_TYPES = ["image/png", "image/svg+xml"];
const MAX_ICON_SIZE_BYTES = 500 * 1024;

// Single shared implementation of the multi custom-icon upload path used by
// Offres / Actions / MaCommande / Publicites — section-icon configuration
// tabs that all accept several user-uploaded icons and let the user pick
// which one is active.
// Consolidates: 3 states (urls / filenames / selected idx), the derived
// `customIconUrl`, the file input ref, the cleanup pattern (ref +
// empty-deps useEffect to avoid revoking still-referenced blob URLs on
// every state change), and the two handlers `handleIconUpload` (append)
// and `removeCustomIcon(idx?)` (indexed remove + active-slot fallback).
export function useMultiCustomIcons({
    saved,
    fallbackIconKey,
    setSelectedIcon,
}: UseMultiCustomIconsOptions): UseMultiCustomIconsResult {
    const { t } = useTranslation();
    const { registerPendingFile, removePendingFile } = React.useContext(
        ScanToContext
    );

    const [customIconUrls, setCustomIconUrls] = React.useState<string[]>(() => {
        const files = saved?.customIconFiles;
        if (Array.isArray(files) && files.length) {
            return files.map(
                (item: any) =>
                    item?.url ||
                    (item?.filename?.startsWith("http") ? item.filename : "")
            );
        }
        const raw = saved?.customIconUrls || saved?.customIconUrl;
        if (Array.isArray(raw))
            return raw.map((u: any) =>
                typeof u === "string" &&
                (u.startsWith("blob:") || u.startsWith("http"))
                    ? u
                    : ""
            );
        if (typeof raw === "string" && raw) return [raw];
        return [];
    });

    const [customIconFilenames, setCustomIconFilenames] = React.useState<
        string[]
    >(() => {
        const raw = saved?.customIconFiles;
        if (Array.isArray(raw))
            return raw.map((item: any) => item?.filename || "");
        return [];
    });

    const [selectedCustomIdx, setSelectedCustomIdx] = React.useState<number>(
        () => {
            const idx = saved?.selectedCustomIdx;
            if (typeof idx === "number" && idx >= 0) return idx;
            return 0;
        }
    );

    const customIconUrl = customIconUrls[selectedCustomIdx] || "";
    const iconFileInputRef = React.useRef<HTMLInputElement>(null);

    // Track the latest array in a ref so the unmount cleanup reads it
    // without re-running on every change to `customIconUrls`. If we put
    // `customIconUrls` in the deps, each upload would fire the cleanup of
    // the previous render and revoke blob URLs that are still referenced
    // by both the new state AND the sessionStorage payload the popup
    // window reads — exactly the bug that affected Offres before the ref
    // pattern was introduced.
    const customIconUrlsRef = React.useRef(customIconUrls);
    React.useEffect(() => {
        customIconUrlsRef.current = customIconUrls;
    }, [customIconUrls]);

    React.useEffect(() => {
        return () => {
            customIconUrlsRef.current.forEach((url) => {
                if (typeof url === "string" && url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!VALID_ICON_MIME_TYPES.includes(file.type)) {
            showErrorToast(t("Invalid file format. Please use PNG or SVG."));
            event.target.value = "";
            return;
        }

        if (file.size > MAX_ICON_SIZE_BYTES) {
            showErrorToast(t("File size is too large. Maximum 500KB."));
            event.target.value = "";
            return;
        }

        const blobUrl = URL.createObjectURL(file);
        registerPendingFile(blobUrl, file);
        // Append the new icon — never change selection, keep whatever is
        // currently selected.
        setCustomIconUrls((prev) => [...prev, blobUrl]);
        setCustomIconFilenames((prev) => [...prev, file.name]);

        event.target.value = "";
    };

    const resetCustomIcons = () => {
        customIconUrls.forEach((url) => {
            if (typeof url === "string" && url.startsWith("blob:")) {
                removePendingFile(url);
                URL.revokeObjectURL(url);
            }
        });
        setCustomIconUrls([]);
        setCustomIconFilenames([]);
        setSelectedCustomIdx(0);
    };

    const removeCustomIcon = (idx?: number) => {
        const target = typeof idx === "number" ? idx : selectedCustomIdx;
        const url = customIconUrls[target];
        if (url) {
            removePendingFile(url);
            URL.revokeObjectURL(url);
        }
        setCustomIconUrls((prev) => prev.filter((_, i) => i !== target));
        setCustomIconFilenames((prev) => prev.filter((_, i) => i !== target));
        setSelectedCustomIdx((prev) => {
            if (prev === target) {
                const remaining = customIconUrls.length - 1;
                if (remaining === 0) setSelectedIcon(fallbackIconKey);
                return 0;
            }
            return prev > target ? prev - 1 : prev;
        });
    };

    return {
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        setSelectedCustomIdx,
        customIconUrl,
        iconFileInputRef,
        handleIconUpload,
        removeCustomIcon,
        resetCustomIcons,
    };
}
